import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface GeneralCatalogSelectOption {
  value: string;
  label: string;
  /** Prefix de negocio del padre (ej. genders) o code del ítem. */
  code?: string;
}

/**
 * Selects unificados de catálogos generales.
 * - GET {apiUrl}/selects/general-catalogs
 * - GET {apiUrl}/selects/general-catalogs/{catalogKey}/lists
 *   (`catalogKey` = prefix de negocio o UUID del padre)
 */
@Injectable({ providedIn: 'root' })
export class GeneralCatalogSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly catalogsCache = new Map<string, GeneralCatalogSelectOption[]>();
  private readonly listsCache = new Map<string, GeneralCatalogSelectOption[]>();

  getCatalogOptions(): Observable<GeneralCatalogSelectOption[]> {
    const url = '/selects/general-catalogs';
    const cached = this.catalogsCache.get(url);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${url}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.catalogsCache.set(url, options)),
        catchError(() => of([])),
      );
  }

  /** Ítems por UUID del padre (útil si ya lo tenés). */
  getListOptionsByCatalogId(catalogId: string): Observable<GeneralCatalogSelectOption[]> {
    return this.getListOptionsByKey(catalogId);
  }

  /**
   * Ítems activos por prefix de negocio (una sola llamada).
   * Ej. getListOptionsByPrefix('genders') → GET .../general-catalogs/genders/lists
   */
  getListOptionsByPrefix(catalogPrefix: string): Observable<GeneralCatalogSelectOption[]> {
    const prefix = (catalogPrefix || '').trim().toLowerCase();
    if (!prefix) return of([]);
    return this.getListOptionsByKey(prefix);
  }

  private getListOptionsByKey(catalogKey: string): Observable<GeneralCatalogSelectOption[]> {
    const key = `/selects/general-catalogs/${encodeURIComponent(catalogKey)}/lists`;
    const cached = this.listsCache.get(key);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${key}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.listsCache.set(key, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(body: unknown): GeneralCatalogSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          text?: string;
          label?: string;
          name?: string;
          code?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
        code: item.code?.trim() || undefined,
      }));
    } catch {
      return [];
    }
  }
}
